import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCartElement
 */

/**
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
export function isCartFlyTargetVisible(element) {
  if (!(element instanceof Element)) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return false;

  const style = getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
}

/**
 * Picks the header cart control that is actually on-screen (desktop vs mobile, RTL included).
 * @returns {HTMLElement | null}
 */
export function getCartFlyDestination() {
  const candidates = document.querySelectorAll('.header-actions__cart-icon, cart-icon');
  /** @type {HTMLElement | null} */
  let fallback = null;

  for (const element of candidates) {
    if (!(element instanceof HTMLElement)) continue;
    fallback = fallback || element;
    if (isCartFlyTargetVisible(element)) return element;
  }

  return fallback;
}

/**
 * @param {Element | null | undefined} trigger
 * @returns {string}
 */
export function getCartFlyImageUrl(trigger) {
  if (!(trigger instanceof Element)) return '';

  const component = trigger.closest('add-to-cart-component');
  const fromDataset = component instanceof HTMLElement ? component.dataset.productVariantMedia : '';
  if (fromDataset) return fromDataset;

  const root = trigger.closest(
    'product-card, .product-card, .product-showcase, .product-information, sticky-add-to-cart, [data-product-id]'
  );
  const image = root?.querySelector('.card-gallery img, product-media img, [ref="productImage"], img');
  if (image instanceof HTMLImageElement) {
    return image.currentSrc || image.src || '';
  }

  return '';
}

/**
 * @param {object} options
 * @param {Element} options.source
 * @param {Element | null} [options.destination]
 * @param {string} [options.imageUrl]
 * @param {string} [options.variant]
 * @param {boolean | string} [options.useSourceSize]
 * @returns {FlyToCartElement | null}
 */
export function launchFlyToCart({ source, destination, imageUrl, variant, useSourceSize }) {
  const cartIcon = destination instanceof Element ? destination : getCartFlyDestination();
  if (!(source instanceof Element) || !cartIcon) return null;

  const flyToCartElement = /** @type {FlyToCartElement} */ (document.createElement('fly-to-cart'));
  flyToCartElement.setAttribute('dir', 'ltr');
  flyToCartElement.classList.add(variant || 'fly-to-cart--main');

  if (imageUrl) {
    flyToCartElement.style.setProperty('background-image', `url("${imageUrl.replace(/"/g, '\\"')}")`);
  }

  flyToCartElement.source = source;
  flyToCartElement.destination = cartIcon;
  if (useSourceSize) flyToCartElement.useSourceSize = useSourceSize;

  document.body.appendChild(flyToCartElement);
  return flyToCartElement;
}

/**
 * FlyToCart custom element for animating product images to cart.
 * Uses viewport coordinates so the path stays correct in RTL and when html/body
 * overflow creates a fixed containing block (Arabic mobile).
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean | string} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('dir', 'ltr');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#start());
    });
  }

  #start() {
    if (!(this.source instanceof Element)) {
      this.remove();
      return;
    }

    let destination = this.destination;
    if (!isCartFlyTargetVisible(destination)) {
      destination = getCartFlyDestination() || destination;
      this.destination = destination;
    }

    if (!(destination instanceof Element)) {
      this.remove();
      return;
    }

    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = destination.getBoundingClientRect();

    if (sourceRect.width === 0 && sourceRect.height === 0) {
      this.remove();
      return;
    }

    this.#animate(sourceRect, destinationRect);
  }

  /**
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize && this.useSourceSize !== 'false') {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }

    this.style.translate = 'none';
    this.style.transform = 'none';
    this.style.left = '0px';
    this.style.top = '0px';
    this.style.right = 'auto';

    const origin = this.getBoundingClientRect();
    const width = this.offsetWidth || sourceRect.width || 40;
    const height = this.offsetHeight || sourceRect.height || 40;
    const startX = startPoint.x - origin.left - width / 2;
    const startY = startPoint.y - origin.top - height / 2;
    const endX = endPoint.x - origin.left - width / 2;
    const endY = endPoint.y - origin.top - height / 2;
    const deltaX = endX - startX;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const pad = Math.max(8, width / 2);
    const arcLift = Math.min(88, Math.max(32, Math.abs(deltaX) * 0.2 + 36));

    let midX = startX + deltaX * 0.5;
    let midY = Math.min(startY, endY) - arcLift;
    midX = Math.min(Math.max(midX, pad - width / 2), viewportWidth - pad - width / 2);
    midY = Math.min(Math.max(midY, pad - height / 2), viewportHeight - pad - height / 2);

    this.style.transform = `translate(${startX}px, ${startY}px)`;
    this.style.opacity = '1';

    await yieldToMainThread();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduceMotion ? 1 : this.classList.contains('fly-to-cart--sticky') ? 800 : 650;

    const animation = this.animate(
      [
        {
          transform: `translate(${startX}px, ${startY}px) scale(1)`,
          opacity: 1,
          offset: 0,
        },
        {
          transform: `translate(${midX}px, ${midY}px) scale(0.7)`,
          opacity: 1,
          offset: 0.55,
        },
        {
          transform: `translate(${endX}px, ${endY}px) scale(0.2)`,
          opacity: 0.25,
          offset: 1,
        },
      ],
      {
        duration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    try {
      await animation.finished;
    } catch {
      // Animation was cancelled (element removed).
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
